import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faLocationDot,
  faClock,
} from '@fortawesome/pro-regular-svg-icons';
import { useClientStore } from '@core/client/useClientStore';
import { Job } from '@core/generated/gql-types';
import { getWorkingDaysBadgesForJob } from '@core/job/workingDays/workingDays.util';
import { useJobUtils } from '@/modules/jobs/hooks/useJobUtils';
import { Link } from 'react-router-dom';
import { formatJobTime12h } from '@/modules/jobs/utils/jobTime.util';
import {
  getJobDisplayScheduleEndDay,
  getJobDisplayScheduleStartDay,
  getShiftDurationHoursFromJobDays,
  formatShiftDurationHoursLabel,
} from '@core/util/jobScheduleBounds.util';
import WorkingDaysDisplay from '../WorkingDaysDisplay';

interface DetailsSectionProps {
  job: Job;
  showClientName?: boolean;
}

const DetailsSection = ({
  job,
  showClientName = true,
}: DetailsSectionProps) => {
  const { getClientById } = useClientStore();

  const { getStatusBadgeClass } = useJobUtils();

  const client = getClientById(job.clientId);

  const clientName = client?.name || 'Unknown Client';

  const clientLogoUrl = client?.logoUrl;

  const startDay = getJobDisplayScheduleStartDay(job);
  const endDisplayDay = getJobDisplayScheduleEndDay(job);

  const dateRangeLabel =
    startDay && endDisplayDay && startDay.isSame(endDisplayDay, 'day')
      ? endDisplayDay.format('D MMMM YYYY')
      : startDay && endDisplayDay
        ? `${startDay.format('D MMM')} – ${endDisplayDay.format('D MMM YYYY')}`
        : 'Not set';

  const visibleWorkingDaysBadges =
    startDay && endDisplayDay
      ? getWorkingDaysBadgesForJob(job, startDay, endDisplayDay)
      : [];

  const startTimeRaw = job.days?.startTime || job.clockInTime;
  const startTimeFormatted = formatJobTime12h(startTimeRaw) ?? 'Not set';

  const endTimeRaw = job.days?.endTime || job.clockOutTime;
  const endTimeFormatted = formatJobTime12h(endTimeRaw) ?? 'Not set';

  const durationLabel = formatShiftDurationHoursLabel(
    getShiftDurationHoursFromJobDays(job),
  );

  const address = [job.address?.addressLine1 && job.address?.addressLine2]
    .filter(Boolean)
    .join(', ');

  const location =
    job.address?.addressFriendly || job.addressHash || 'Job address not set';

  const locationString = address ? `${address}, ${location}` : location;

  return (
    <div className="space-y-3 rounded-md bg-white p-2">
      {/* Job Header with Logo and Title */}
      <div className="flex items-start justify-between p-1">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-gray-200">
            {clientLogoUrl && (
              <img
                src={clientLogoUrl}
                alt={clientName}
                className="h-14 w-14 object-contain"
              />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-md font-bold text-gray-900">
                {job.title || 'Untitled Job'}
              </h2>
            </div>
            {showClientName ? (
              <div className="mt-1 text-sm text-gray-600">
                <Link
                  to={`/clients/${job.clientId}`}
                  className="text-gray-600 hover:text-gray-900 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {clientName}
                </Link>
              </div>
            ) : null}
          </div>
        </div>
        <span
          className={`flex rounded-full px-2 py-1 text-xs font-medium ${getStatusBadgeClass(job.itemStatus || undefined)}`}
        >
          {job.itemStatus || ''}
        </span>
      </div>

      {/* Job Details */}
      <div className="flex flex-col items-start justify-center gap-1">
        <div className="flex flex-row items-start gap-2 text-sm font-medium">
          <FontAwesomeIcon icon={faCalendar} className="mt-0.5 text-gray-600" />
          <div>
            <span>{dateRangeLabel}</span>
            {visibleWorkingDaysBadges.length > 0 && (
              <div className="mt-1.5">
                <WorkingDaysDisplay badges={visibleWorkingDaysBadges} />
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-row items-center gap-2 text-sm font-medium">
          <FontAwesomeIcon icon={faClock} className="text-gray-700" />
          <span>
            {`${startTimeFormatted} – ${endTimeFormatted} `}
            <span className="font-normal text-gray-400">
              {durationLabel ? `(${durationLabel})` : '(Not set)'}
            </span>
          </span>{' '}
        </div>
        <div className="flex flex-row items-center gap-2 text-sm font-medium">
          <FontAwesomeIcon icon={faLocationDot} className="text-gray-600" />
          <span>{locationString}</span>
        </div>
      </div>
    </div>
  );
};

export default DetailsSection;
